<template>
    <div v-if="loaded">
        <rkm-area
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.unidadeId"
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.equipes"
                            @change="handleEquipeChange"
                            default-option-text="Todas"
                            label="Equipe"
                            show-default-option
                            text-field="descricao"
                            v-model="model.equipeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.ocupacoes"
                            @change="handleOcupacaoChange"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.procedencias"
                            @change="handleProcedenciaChange"
                            default-option-text="Todas"
                            label="Procedência"
                            show-default-option
                            text-field="descricao"
                            v-model="model.procedenciaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.conclusoes"
                            @change="handleConclusaoChange"
                            default-option-text="Todas"
                            label="Conclusão"
                            show-default-option
                            text-field="descricao"
                            v-model="model.conclusaoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input-autocomplete
                            :disabled="!hasPeriodo"
                            :repository="{ autocomplete: repositories.relatorioAvaliacaoElegibilidadeAdmissao.autocompletePaciente }"
                            :request-query-string-params="autocompletePacienteParams"
                            :search-fields="pacienteSearchFields"
                            label="Paciente"
                            placeholder="Nome, Nome social, CNS, CPF, Data de nascimento ou Prontuário"
                            text-field="nome"
                            v-model="model.paciente"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="clear"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            v-if="itens.length > 0 && totalizadores"
            class="totalizadores"
            title="Totalizadores"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Total de avaliações"
                        v-model="totalizadores.totalAvaliacoes"
                    />
                </rkm-col>

                <rkm-col
                    v-for="conclusao in totalizadores.conclusoes"
                    :key="conclusao.descricao"
                >
                    <rkm-form-input
                        :label="conclusao.descricao"
                        disabled
                        v-model="conclusao.total"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Resultado">
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
            />
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { error, file, formats, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

export default {
    computed: {
        validations() {
            return {
                periodo: { required: true },
            }
        },
        autocompletePacienteParams() {
            if (!this.hasPeriodo) return {}

            return this.camelObjectKeysToSnakeRecursive(this.getFilterParams())
        },
        pacienteSearchFields() {
            return [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.nome_social', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'cidadao.data_nascimento', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ]
        },
        columns() {
            return [
                {
                    key: 'data',
                    label: 'Data',
                    class: 'col-min text-center',
                    fixed: true,
                    formatter: v => this.formatDate(v),
                    filterByFormatted: true,
                    sortable: true
                },
                { key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true },
                { key: 'infraestruturaNome', label: 'Local de atendimento', sortable: true },
                { key: 'usuarioNome', label: 'Profissional', sortable: true },
                { key: 'equipeDescricao', label: 'Equipe', sortable: true },
                { key: 'ocupacaoDescricao', label: 'CBO', sortable: true },
                { key: 'pacienteNome', label: 'Paciente', sortable: true },
                { key: 'procedenciaDescricao', label: 'Procedência', sortable: true },
                { key: 'condicoesAvaliadas', label: 'Condições avaliadas', sortable: false },
                { key: 'cids', label: 'CIDs', sortable: false },
                { key: 'conclusaoDescricao', label: 'Conclusão', sortable: true },
            ]
        },
        fields() {
            return this.columns.filter(c => c.fixed || this.selectedCols.includes(c.key))
        },
        optionalColumns() {
            return this.columns.filter(c => !c.fixed)
        },
        hasPeriodo() {
            return this.model.periodo.start !== null && this.model.periodo.end !== null
        },
    },
    created() {
        this.repositories = {
            relatorioAvaliacaoElegibilidadeAdmissao: this.getConectaSusRepository('atencaoBasica.relatorioAvaliacaoElegibilidadeAdmissao'),
            impressao: this.getConectaSusRepository('impressao.atencaoBasica'),
            exportacao: this.getConectaSusRepository('exportacao.atencaoBasica'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        itens: [],
        totalizadores: null,
        sortBy: 'data',
        sortDesc: false,
        selectedCols: [
            'pacienteNome',
            'procedenciaDescricao',
            'condicoesAvaliadas',
            'cids',
            'conclusaoDescricao'
        ],
        selectedAll: false,
        indeterminate: true,
        others: { moment },
        unwatchers: {
            unwatchPeriodo: null,
            unwatchPaciente: null,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioAvaliacaoElegibilidadeAdmissao.create()
                .then(({ data }) => {
                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.loaded = true

                    this.addWatchers()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {
            this.unwatchers.unwatchPeriodo = this.$watch('model.periodo', {
                deep: true,
                handler: this.handlePeriodoChange,
            })

            this.unwatchers.unwatchPaciente = this.$watch('model.paciente', this.handlePacienteChange)
        },
        handlePeriodoChange(periodo) {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.equipes = []
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.equipeId = null
            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            if (periodo.start === null || periodo.end === null) return

            this.loadFromUnidade()
        },
        handleUnidadeChange() {
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.equipes = []
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.equipeId = null
            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromInfraestrutura()
        },
        handleInfraestruturaChange() {
            this.form.usuarios = []
            this.form.equipes = []
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.usuarioId = null
            this.model.equipeId = null
            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromUsuario()
        },
        handleUsuarioChange() {
            this.form.equipes = []
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.equipeId = null
            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromEquipe()
        },
        handleEquipeChange() {
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromOcupacao()
        },
        handleOcupacaoChange() {
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromProcedencia()
        },
        handleProcedenciaChange() {
            this.form.conclusoes = []

            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.loadFromConclusao()
        },
        handleConclusaoChange() {
            this.model.paciente = null

            this.clearResult()
        },
        handlePacienteChange() {
            this.clearResult()
        },
        loadFromUnidade() {
            this.loadList('unidades', data => {
                this.form.unidades = data.unidades
                this.form.infraestruturas = data.infraestruturas
                this.form.usuarios = data.usuarios
                this.form.equipes = data.equipes
                this.form.ocupacoes = data.ocupacoes
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromInfraestrutura() {
            this.loadList('infraestruturas', data => {
                this.form.infraestruturas = data.infraestruturas
                this.form.usuarios = data.usuarios
                this.form.equipes = data.equipes
                this.form.ocupacoes = data.ocupacoes
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromUsuario() {
            this.loadList('usuarios', data => {
                this.form.usuarios = data.usuarios
                this.form.equipes = data.equipes
                this.form.ocupacoes = data.ocupacoes
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromEquipe() {
            this.loadList('equipes', data => {
                this.form.equipes = data.equipes
                this.form.ocupacoes = data.ocupacoes
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromOcupacao() {
            this.loadList('ocupacoes', data => {
                this.form.ocupacoes = data.ocupacoes
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromProcedencia() {
            this.loadList('procedencias', data => {
                this.form.procedencias = data.procedencias
                this.form.conclusoes = data.conclusoes
            })
        },
        loadFromConclusao() {
            this.loadList('conclusoes', data => {
                this.form.conclusoes = data.conclusoes
            })
        },
        loadList(loadFrom, onLoad) {
            const params = {
                loadFrom,
                ...this.getFilterParams(),
            }

            this.setLoading()

            this.repositories.relatorioAvaliacaoElegibilidadeAdmissao.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => onLoad(data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            this.setLoading()

            this.repositories.relatorioAvaliacaoElegibilidadeAdmissao.index(this.prepareData())
                .then(({ data }) => {
                    this.itens = data.itens
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        print() {
            this.setLoading()

            this.repositories.impressao.relatorioAvaliacaoElegibilidadeAdmissao(this.prepareData(true))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            this.repositories.exportacao.relatorioAvaliacaoElegibilidadeAdmissao(this.prepareData(true))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData(isPrintExport = false) {
            const params = {
                ...this.getFilterParams(),
                pacienteId: this.model.paciente?.id ?? null,
            }

            if (isPrintExport) {
                params.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
                params.sortDesc = this.sortDesc

                params.columns = this.fields.map(f => ({
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class,
                }))
            }

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        getFilterParams() {
            return {
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                equipeId: this.model.equipeId,
                ocupacaoId: this.model.ocupacaoId,
                procedenciaId: this.model.procedenciaId,
                conclusaoId: this.model.conclusaoId,
            }
        },
        getPeriodo() {
            return {
                start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
            }
        },
        clear() {
            this.removeWatchers()

            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.equipes = []
            this.form.ocupacoes = []
            this.form.procedencias = []
            this.form.conclusoes = []

            this.model.periodo = { start: null, end: null }
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.equipeId = null
            this.model.ocupacaoId = null
            this.model.procedenciaId = null
            this.model.conclusaoId = null
            this.model.paciente = null

            this.clearResult()

            this.$nextTick().then(() => {
                this.addWatchers()
                this.$refs.Form.resetValidator()
            })
        },
        removeWatchers() {
            this.unwatchers.unwatchPeriodo()
            this.unwatchers.unwatchPaciente()
        },
        clearResult() {
            this.itens = []
            this.totalizadores = null
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        selectAll(selected) {
            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [error, file, formats, repository, stringCase],
    watch: {
        selectedCols(selectedCols) {
            const total = this.optionalColumns.length

            this.selectedAll = selectedCols.length === total
            this.indeterminate = selectedCols.length > 0 && selectedCols.length < total
        },
    },
}
</script>

<style scoped>
.totalizadores >>> input {
    font-weight: bold;
}
</style>
